import type { FastifyRequest, FastifyReply } from 'fastify'
import { z } from 'zod'
import * as Sentry from '@sentry/node'
import { createLogger } from '@/common/utils/logger.js'

const logger = createLogger('LoggingController')

/**
 * Schema for console error log endpoint
 */
const ConsoleErrorSchema = z.object({
  level: z.enum(['error', 'warn']),
  context: z.object({
    message: z.string(),
    stack: z.string(),
    causeChain: z.array(z.string()),
    timestamp: z.string(),
    userAgent: z.string(),
    url: z.string(),
    userId: z.string(),
    sessionId: z.string()
  }),
  timestamp: z.number()
})

const BatchSchema = z.object({
  errors: z.array(ConsoleErrorSchema),
  source: z.literal('browser-console')
})

export async function handleConsoleErrors(
  request: FastifyRequest,
  reply: FastifyReply
): Promise<void> {
  try {
    const body = BatchSchema.parse(request.body)
    const userId = request.user?.uid ?? 'anonymous'

    // Log to console for immediate visibility
    logger.warn('[Console Errors from Browser] Batch received', {
      userId,
      errorCount: body.errors.length,
      errors: body.errors.map(e => ({
        level: e.level,
        message: e.context.message,
        url: e.context.url
      }))
    })

    // Send critical errors to Sentry
    for (const error of body.errors) {
      if (error.level === 'error') {
        Sentry.captureMessage(
          `[Browser Error] ${error.context.message}`,
          {
            level: 'error',
            tags: {
              source: 'browser-console',
              userId,
              sessionId: error.context.sessionId
            },
            extra: {
              stack: error.context.stack,
              causeChain: error.context.causeChain,
              userAgent: error.context.userAgent,
              url: error.context.url
            }
          }
        )
      }
    }

    // Return success
    reply.code(202).send({ received: body.errors.length })
  } catch (err) {
    logger.error('Failed to process console errors', { error: err })
    reply.code(400).send({ error: 'Invalid request format' })
  }
}
